import React from 'react';
import { stripHtml } from './stripHtml';
import '../styles/exampleText.css';

// Example sentences are stored as HTML: the target word wrapped in <em> (from
// Reverso or the editor, which may add attributes or uppercase the tag), and
// Reword imports append the translation after a " - " separator. Same rules as
// weavewords-mobile's components/HighlightedText.tsx — keep the two in sync.

/** Matches one <em>…</em>, any case/attributes, capturing its inner HTML. */
const EM_RE = /<em\b[^>]*>([\s\S]*?)<\/em>/gi;

/** Matches an opening or closing <em> tag. */
export const EM_TAG_RE = /<\/?em\b[^>]*>/gi;

/** One HTML entity (&nbsp; &amp; &#39; …), masked out before separator matching. */
const ENTITY_RE = /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi;

/**
 * "<sentence> - <translation>" separator: a dash with whitespace on both
 * sides, or after sentence-ending punctuation (group 1, which stays with the
 * sentence) with whitespace on at least one side of the dash ("Labor.
 * -Медалью", "word.- translation" — but not "U.S.-based"), or after ";". A
 * bare "-word" ("und -druck", "-18C") is not one. A plain spaced dash used as punctuation is
 * indistinguishable from Reword's " - " format and still splits.
 */
const EXAMPLE_SEP_RE = /\s+-\s+(?=\S)|([.!?…"'»”)\]])(?:\s+-\s*|-\s+)(?=\S)|;\s*-\s*(?=\S)/;

/**
 * Splits a stored example at its first "<sentence> - <translation>" separator,
 * or returns null when it has none. Matching runs on a copy with every entity
 * masked to the same length (&nbsp; as spaces, others as "_"), so an entity's
 * closing ";" is never taken for a separator, while indexes still map onto the
 * original HTML. No lookbehind, so mobile (Hermes) can share it verbatim.
 */
export function splitExample(example: string): { sentence: string; translation: string } | null {
    const masked = example.replace(ENTITY_RE, e => (e.toLowerCase() === '&nbsp;' ? ' ' : '_').repeat(e.length));
    const m = EXAMPLE_SEP_RE.exec(masked);
    if (!m) return null;
    return {
        sentence: example.slice(0, m.index + (m[1]?.length ?? 0)),
        translation: example.slice(m.index + m[0].length),
    };
}

export function stripEmTags(str: string): string {
    return str.replace(EM_TAG_RE, '');
}

function renderHighlighted(html: string): React.ReactNode[] {
    // split() with a capture group puts each <em>'s inner HTML at the odd indices.
    return html.split(EM_RE).map((part, i) =>
        i % 2 ? <em key={i} className="ex-highlight">{stripHtml(part)}</em> : stripHtml(part)
    );
}

/**
 * Renders a stored example as React text — never innerHTML, since examples can
 * be other users' content (public word pages). The <em> word is highlighted;
 * a trailing translation is set off by a separator and left unhighlighted.
 */
export function renderExample(example: string): React.ReactNode {
    const parts = splitExample(example);
    if (!parts) return renderHighlighted(example);
    const { sentence, translation } = parts;
    return (
        <>
            {renderHighlighted(sentence)}
            <span className="ex-sep"> — </span>
            <span className="ex-translation">{stripHtml(translation)}</span>
        </>
    );
}
